// Rank-domain backdrop math for the scan-window scrubber.
//
// The scan window pages the scope by RANK (an offset into the newest-first
// prompt list), so the scrubber rail is rank-linear: equal width = equal
// prompt count, and the brush — a fixed prompt-count window — keeps a
// constant width wherever it sits. The graph behind the brush (token volume,
// falling back to prompt volume when no usage rows exist) comes from
// TIME-bucketed aggregates, which `resampleToRank` redistributes into rank
// space: a time bucket holding C prompts covers a C-prompt span of ranks,
// and its volume spreads across that span. A weekend lull therefore takes
// almost no rail width, and a bursty hour stretches wide — exactly the
// distortion that makes every brush position hold real prompts.
//
// Pure functions; the analytics fetch lives in apiClient's getScanVolume.

import { tokenVolumeOf } from "./tokenVolume.js";
import { parseServerTimestamp } from "./traceUtils.js";

/**
 * Align raw per-bucket aggregate rows onto a dense chronological grid.
 * Bucket index 0 = oldest (fromMs); values land via the same floor-index
 * mapping densifyBuckets uses.
 * @returns {{ counts: number[], tokens: number[], fromMs: number, toMs: number, stepMs: number }}
 */
export function shapeScanVolume({ countRows = [], tokenRows = [], bucketCount = 0, bucketSeconds = 1, fromMs = 0, toMs = 0 } = {}) {
    const stepMs = Math.max(1, (bucketSeconds || 1) * 1000);
    const counts = new Array(bucketCount).fill(0);
    const tokens = new Array(bucketCount).fill(0);
    const outputTokens = new Array(bucketCount).fill(0);
    const indexOf = (r) => {
        // parseServerTimestamp, not Date.parse: bucket starts arrive as
        // zone-less ClickHouse strings; reading them as local shifts each
        // bucket off the true-epoch `fromMs` grid (same trap densifyBuckets
        // documents). Numeric buckets pass straight through.
        const bMs = parseServerTimestamp(r.bucket);
        if (!Number.isFinite(bMs)) return -1;
        return Math.max(0, Math.min(bucketCount - 1, Math.floor((bMs - fromMs) / stepMs)));
    };
    for (const r of countRows) {
        const i = indexOf(r);
        if (i >= 0) counts[i] += Number(r.count) || 0;
    }
    for (const r of tokenRows) {
        const i = indexOf(r);
        if (i < 0) continue;
        // Total volume = the TOKEN_VOLUME_ALIASES sum (input + output + the
        // provider-predicated cache sums; tokenVolume.js has the accounting
        // rationale). Cache aliases absent or NULL — non-Anthropic rows,
        // pre-O-2047 callers — contribute zero.
        tokens[i] += tokenVolumeOf(r);
        outputTokens[i] += Number(r.output_tokens) || 0;
    }
    return { counts, tokens, outputTokens, fromMs, toMs, stepMs };
}

/**
 * Resample a time-bucketed volume into `rankBuckets` equal-PROMPT-COUNT bars.
 * Each time bucket's volume is distributed over the rank span its prompts
 * occupy, proportionally to overlap. Bars are chronological: bar 0 = the
 * scope's oldest prompts (matching the rail's left = oldest orientation).
 *
 * `metric` picks the bar source: "tokens" (total token volume, the default),
 * "output_tokens", or "prompts" (the counts themselves). A token metric
 * with no usage rows falls back to counts so the rail never goes blank
 * for scopes without usage data.
 *
 * mode: the source actually used — "tokens" | "output_tokens" | "prompts" —
 * or "empty" (no prompts — caller degrades to a plain rail).
 * @returns {{ bars: number[], mode: string, totalCount: number }}
 */
export function resampleToRank(volume, rankBuckets, metric = "tokens") {
    const counts = volume?.counts || [];
    const bars = new Array(rankBuckets).fill(0);
    let totalCount = 0;
    for (const c of counts) totalCount += c;
    if (!totalCount || rankBuckets <= 0) return { bars, mode: "empty", totalCount: 0 };

    const requested = metric === "prompts"
        ? counts
        : (metric === "output_tokens" ? volume.outputTokens : volume.tokens) || [];
    let requestedTotal = 0;
    for (const t of requested) requestedTotal += t;
    const usingRequested = metric === "prompts" || requestedTotal > 0;
    const source = usingRequested ? requested : counts;
    const mode = usingRequested ? metric : "prompts";

    const per = totalCount / rankBuckets; // prompts per rank bar
    let cum = 0;
    for (let i = 0; i < counts.length; i++) {
        const c = counts[i];
        if (!c) continue;
        const v = source[i] || 0;
        const spanStart = cum;
        const spanEnd = cum + c;
        // 1e-9 keeps an exact-boundary span out of the next bar.
        const b0 = Math.min(rankBuckets - 1, Math.floor(spanStart / per));
        const b1 = Math.min(rankBuckets - 1, Math.floor((spanEnd - 1e-9) / per));
        if (b0 === b1) {
            bars[b0] += v;
        } else {
            for (let b = b0; b <= b1; b++) {
                const lo = Math.max(spanStart, b * per);
                const hi = Math.min(spanEnd, (b + 1) * per);
                if (hi > lo) bars[b] += (v * (hi - lo)) / c;
            }
        }
        cum = spanEnd;
    }
    return { bars, mode, totalCount };
}

/**
 * Project a rail fraction (0 = oldest prompt, 1 = newest) to a timestamp by
 * walking the cumulative prompt counts and interpolating inside the bucket.
 * Lets the scrubber read out the window's projected date span mid-drag,
 * before the window actually fetches. Returns null when the scope is empty.
 */
export function timeAtRankFrac(volume, frac) {
    const counts = volume?.counts || [];
    let total = 0;
    for (const c of counts) total += c;
    if (!total) return null;
    const target = Math.min(total, Math.max(0, frac * total));
    let cum = 0;
    for (let i = 0; i < counts.length; i++) {
        const c = counts[i];
        if (!c) continue;
        if (cum + c >= target) {
            const within = (target - cum) / c;
            return volume.fromMs + (i + within) * volume.stepMs;
        }
        cum += c;
    }
    return volume.toMs;
}
